import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/context/AuthContext";
import { AnimatedLogoIntro } from "@/components/AnimatedLogoIntro";
import Home from "@/pages/Home";
import Reconnection from "@/pages/Reconnection";
import Classes from "@/pages/Classes";
import Book from "@/pages/Book";
import BookLocation from "@/pages/BookLocation";
import About from "@/pages/About";
import RootedInBody from "@/pages/RootedInBody";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import LegalDoc from "@/pages/LegalDoc";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

const TITLES = {
  "/": "I•AMovement — Movement designed for how humans are built to function",
  "/the-reconnection": "The Reconnection — I•AMovement",
  "/classes": "Classes — I•AMovement",
  "/book": "Book — I•AMovement",
  "/about": "About Alexia, Founder — I•AMovement",
  "/rooted-in-the-body": "Rooted in the Body — The Science — I•AMovement",
  "/contact": "Contact — I•AMovement",
  "/legal": "Legal & Copyright — I•AMovement",
  "/privacy-policy": "Privacy Policy — I•AMovement",
  "/cookie-policy": "Cookie Policy — I•AMovement",
  "/terms": "Terms & Conditions — I•AMovement",
  "/cancellation-refund": "Cancellation, Credit & Refund Policy — I•AMovement",
  "/health-disclaimer": "Health & Fitness Disclaimer — I•AMovement",
  "/acceptable-use": "Acceptable Use Policy — I•AMovement",
};

function SEO() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = TITLES[pathname] || "I•AMovement";
    const desc = "I•AMovement is a movement education system that reconnects people with how their body naturally works — mobility, strength, coordination, balance and nervous system regulation.";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = desc;
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <SEO />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/the-reconnection" element={<Reconnection />} />
        <Route path="/classes" element={<Classes />} />
        <Route path="/book" element={<Book />} />
        <Route path="/book/:locationId" element={<BookLocation />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/about" element={<About />} />
        <Route path="/rooted-in-the-body" element={<RootedInBody />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/privacy-policy" element={<LegalDoc title="Privacy Policy" slug="privacy-policy" />} />
        <Route path="/cookie-policy" element={<LegalDoc title="Cookie Policy" slug="cookie-policy" />} />
        <Route path="/terms" element={<LegalDoc title="Terms & Conditions" slug="terms" />} />
        <Route path="/cancellation-refund" element={<LegalDoc title="Cancellation, Credit & Refund Policy" slug="cancellation-refund" />} />
        <Route path="/health-disclaimer" element={<LegalDoc title="Health & Fitness Disclaimer" slug="health-disclaimer" />} />
        <Route path="/acceptable-use" element={<LegalDoc title="Acceptable Use Policy" slug="acceptable-use" />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/login" element={<AdminLogin />} />
      </Routes>
    </>
  );
}

function App() {
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return false;
    if (window.location.pathname.startsWith("/admin")) return false;
    if (window.location.search.includes("nointro")) return false;
    return !sessionStorage.getItem("iam_intro_seen");
  });

  const done = () => { sessionStorage.setItem("iam_intro_seen", "1"); setShowIntro(false); };

  return (
    <AuthProvider>
      <BrowserRouter>
        <AnimatePresence>{showIntro && <AnimatedLogoIntro onDone={done} />}</AnimatePresence>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
